import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useCallback } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import {
  ImportActBlueDonationHistoryMutation,
  ImportActBlueDonationHistoryMutationVariables,
  IntegrationServiceType,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { InternalLink } from "~frontend/components/Link";
import {
  RhfErrorBanner,
  RhfSelect,
} from "~frontend/components/forms/react-hook-form-components";
import {
  DateRangeSchema,
  RhfDateRangePicker,
} from "~frontend/components/forms/react-hook-form-components/RhfDateRangePicker";
import { SEARCH_DONATION_HISTORY } from "~frontend/graphql/donationHistory";
import {
  GET_ACT_BLUE_DONATION_HISTORY_IMPORTS,
  IMPORT_ACT_BLUE_DONATION_HISTORY,
} from "~frontend/graphql/integration";
import { useIntegrations } from "~frontend/hooks/useIntegrations";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

const FormSchema = z.object({
  actBlueImportIntegrationId: z.string().uuid(),
  dateRange: DateRangeSchema,
});

type FormValues = z.infer<typeof FormSchema>;

export function ImportDonationHistoryDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  const { subaccount, subaccountLink } = useSubaccount()!;

  const { control, handleSubmit, formState, setError, reset } = useForm({
    resolver: zodResolver(FormSchema),
  });

  const actBlueImportIntegrationsQuery = useIntegrations({
    subaccountId: subaccount.id,
    service: IntegrationServiceType.ActBlueImport,
  });

  const [importActBlueDonationHistory, { loading: importRequestLoading }] =
    useMutation<
      ImportActBlueDonationHistoryMutation,
      ImportActBlueDonationHistoryMutationVariables
    >(IMPORT_ACT_BLUE_DONATION_HISTORY, {
      refetchQueries: [
        SEARCH_DONATION_HISTORY,
        GET_ACT_BLUE_DONATION_HISTORY_IMPORTS,
      ],
      awaitRefetchQueries: true,
    });

  const handleImportActBlueDonationHistory = useCallback(
    async (values: FormValues) => {
      const { errors } = await importActBlueDonationHistory({
        variables: {
          input: {
            integrationId: values.actBlueImportIntegrationId,
            startDate: values.dateRange.startDate.toISODate()!,
            endDate: values.dateRange.endDate.toISODate()!,
          },
        },
      });

      if (errors) {
        setError("root", { message: getErrorMessage(errors) });
      } else {
        reset();
        onClose();
      }
    },
    [importActBlueDonationHistory, setError, getErrorMessage, reset, onClose]
  );

  const hasFormErrors = !!Object.keys(formState.errors).length;

  return (
    <Dialog open={open}>
      <DialogTitle>Import Donation History</DialogTitle>
      <form onSubmit={handleSubmit(handleImportActBlueDonationHistory)}>
        <DialogContent>
          <Typography variant="body1" sx={{ marginBottom: "16px" }}>
            Import past donation history to use in targeting and messaging on
            Scale To Win Text. To import donation history from ActBlue, you must
            have an ActBlue Import integration set up on the{" "}
            <InternalLink to={subaccountLink("/integrations")}>
              Integrations Page
            </InternalLink>
            .
          </Typography>
          <AsyncWrapper
            data={actBlueImportIntegrationsQuery.integrations}
            loading={actBlueImportIntegrationsQuery.loading}
            errors={actBlueImportIntegrationsQuery.error}
          >
            {integrations => (
              <>
                <RhfErrorBanner control={control} />
                <RhfSelect
                  control={control}
                  name="actBlueImportIntegrationId"
                  label="ActBlue Integration"
                  fullWidth
                >
                  {integrations.length ? (
                    integrations
                      .filter(integration => !integration.deletedAt)
                      .map(integration => (
                        <MenuItem key={integration.id} value={integration.id}>
                          {integration.name}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem disabled>
                      No ActBlue Import integrations available
                    </MenuItem>
                  )}
                </RhfSelect>
                <RhfDateRangePicker
                  label="Date Range"
                  fullWidth
                  control={control}
                  name="dateRange"
                  maxDate={DateTime.now().setZone("America/New_York")}
                />
              </>
            )}
          </AsyncWrapper>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="tertiary" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={hasFormErrors || importRequestLoading}
          >
            Import
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
